import axios from 'axios';
import { API_BASE_URL, getToken, setToken, removeToken, getRefreshToken, setRefreshToken, removeRefreshToken } from '../config/api';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
apiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // If sending multipart/form-data, let axios set the correct Content-Type with boundary
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      try {
        // Axios may use different header casing depending on environment
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      } catch {
        // ignore
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const isRefreshEndpoint = originalRequest?.url?.includes('/auth/refresh') || originalRequest?.url?.includes('/auth/login');

    // Don't retry on login or refresh endpoints to avoid loops
    if (isRefreshEndpoint) {
      return Promise.reject(error);
    }

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = getRefreshToken();
        if (refreshToken) {
          const response = await apiClient.post('/auth/refresh', {
            refreshToken,
          });

          const { token, refreshToken: newRefreshToken } = response.data?.data || response.data;
          if (token) {
            setToken(token);
            if (newRefreshToken) {
              setRefreshToken(newRefreshToken);
            }

            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          }
        }
      } catch (refreshError) {
        // Refresh failed, logout user
        removeToken();
        removeRefreshToken();
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Authentication Services
export const authService = {
  login: async (username, password) => {
    try {
      const response = await apiClient.post('/auth/login', { username, password });
      
      // Handle different response structures
      let token, refreshToken, user;
      if (response.data.data) {
        // If response is wrapped in data
        ({ token, refreshToken, user } = response.data.data);
      } else {
        ({ token, refreshToken, user } = response.data);
      }
      
      if (token) {
        setToken(token);
        if (refreshToken) {
          setRefreshToken(refreshToken);
        }
        return user;
      } else {
        throw new Error('No token received from server');
      }
    } catch (error) {
      console.error('Login API error:', error.response?.data || error.message);
      throw error;
    }
  },

  logout: async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      removeToken();
      removeRefreshToken();
    }
  },

  getCurrentUser: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
};

// Users Services
export const usersService = {
  getAll: async (page = 1, limit = 10, locationId = null) => {
    const params = { page, limit };
    if (locationId) {
      params.locationId = locationId;
    }
    const response = await apiClient.get('/users', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await apiClient.get(`/users/${id}`);
    return response.data.data || response.data;
  },

  create: async (userData) => {
    const response = await apiClient.post('/users', userData);
    return response.data.data || response.data;
  },

  update: async (id, userData) => {
    const response = await apiClient.put(`/users/${id}`, userData);
    return response.data.data || response.data;
  },

  delete: async (id) => {
    const response = await apiClient.delete(`/users/${id}`);
    return response.data;
  },
};

// Cases Services
export const casesService = {
  getAll: async (page = 1, limit = 10, filters = {}) => {
    const params = { page, limit };
    
    // Add filters if provided
    if (filters.caseNumber) {
      params.caseNumber = filters.caseNumber;
    }
    if (filters.referenceLetterNumber) {
      params.referenceLetterNumber = filters.referenceLetterNumber;
    }
    if (filters.dateFrom) {
      params.dateFrom = filters.dateFrom;
    }
    if (filters.dateTo) {
      params.dateTo = filters.dateTo;
    }
    
    const response = await apiClient.get('/cases', { params });
    return response.data;
  },

  getAllWithRelations: async (page = 1, limit = 10, filters = {}) => {
    const params = { page, limit };
    
    // Add filters if provided
    if (filters.caseNumber) {
      params.caseNumber = filters.caseNumber;
    }
    if (filters.referenceLetterNumber) {
      params.referenceLetterNumber = filters.referenceLetterNumber;
    }
    if (filters.dateFrom) {
      params.dateFrom = filters.dateFrom;
    }
    if (filters.dateTo) {
      params.dateTo = filters.dateTo;
    }
    
    const response = await apiClient.get('/cases/all/with-relations', { params });
    return response.data;
  },

  getById: async (id) => {
    const response = await apiClient.get(`/cases/${id}`);
    return response.data.data || response.data;
  },

  create: async (caseData) => {
    const response = await apiClient.post('/cases', caseData);
    return response.data.data || response.data;
  },

  update: async (id, caseData) => {
    const response = await apiClient.put(`/cases/${id}`, caseData);
    return response.data.data || response.data;
  },

  delete: async (id) => {
    const response = await apiClient.delete(`/cases/${id}`);
    return response.data;
  },
};

// Samples Services
export const samplesService = {
  getByCase: async (caseId) => {
    const response = await apiClient.get(`/cases/${caseId}/samples`);
    return response.data.data || response.data;
  },

  getById: async (id) => {
    const response = await apiClient.get(`/samples/${id}`);
    return response.data.data || response.data;
  },

  create: async (sampleData) => {
    const response = await apiClient.post('/samples', sampleData);
    return response.data.data || response.data;
  },

  update: async (id, sampleData) => {
    if (typeof FormData !== 'undefined' && sampleData instanceof FormData) {
      // Laravel method spoofing for multipart requests
      sampleData.append('_method', 'PUT');
      const response = await apiClient.post(`/samples/${id}`, sampleData);
      return response.data.data || response.data;
    }
    const response = await apiClient.put(`/samples/${id}`, sampleData);
    return response.data.data || response.data;
  },

  delete: async (id) => {
    const response = await apiClient.delete(`/samples/${id}`);
    return response.data;
  },
};

// Traces Services
export const tracesService = {
  getByCase: async (caseId) => {
    const response = await apiClient.get(`/cases/${caseId}/traces`);
    return response.data.data || response.data;
  },

  getBySample: async (sampleId) => {
    const response = await apiClient.get(`/samples/${sampleId}/traces`);
    return response.data.data || response.data;
  },

  getById: async (id) => {
    const response = await apiClient.get(`/traces/${id}`);
    return response.data.data || response.data;
  },

  create: async (traceData) => {
    const response = await apiClient.post('/traces', traceData);
    return response.data.data || response.data;
  },

  update: async (id, traceData) => {
    if (typeof FormData !== 'undefined' && traceData instanceof FormData) {
      // Laravel method spoofing for multipart requests
      traceData.append('_method', 'PUT');
      const response = await apiClient.post(`/traces/${id}`, traceData);
      return response.data.data || response.data;
    }
    const response = await apiClient.put(`/traces/${id}`, traceData);
    return response.data.data || response.data;
  },

  delete: async (id) => {
    const response = await apiClient.delete(`/traces/${id}`);
    return response.data;
  },
};

// Locations Services
export const locationsService = {
  getAll: async (page = 1, limit = 10) => {
    const response = await apiClient.get('/locations', {
      params: { page, limit }
    });
    return response.data;
  },

  getById: async (id) => {
    const response = await apiClient.get(`/locations/${id}`);
    return response.data.data || response.data;
  },

  create: async (locationData) => {
    const response = await apiClient.post('/locations', locationData);
    return response.data.data || response.data;
  },

  update: async (id, locationData) => {
    const response = await apiClient.put(`/locations/${id}`, locationData);
    return response.data.data || response.data;
  },

  delete: async (id) => {
    const response = await apiClient.delete(`/locations/${id}`);
    return response.data;
  },
};

// Static Data Services
export const staticDataService = {
  getRegions: async () => {
    const response = await apiClient.get('/static/regions');
    return response.data.data || response.data;
  },

  getCities: async (regionId) => {
    const response = await apiClient.get(`/static/cities?region=${regionId}`);
    return response.data.data || response.data;
  },
};

// Audit Logs Services
export const auditLogsService = {
  getByCase: async (caseId) => {
    const response = await apiClient.get(`/cases/${caseId}/audit-logs`);
    return response.data.data || response.data;
  },

  getAllByCase: async (caseId) => {
    const response = await apiClient.get(`/cases/${caseId}/audit-logs/all`);
    return response.data.data || response.data;
  },

  getBySample: async (sampleId) => {
    const response = await apiClient.get(`/samples/${sampleId}/audit-logs`);
    return response.data.data || response.data;
  },

  getByTrace: async (traceId) => {
    const response = await apiClient.get(`/traces/${traceId}/audit-logs`);
    return response.data.data || response.data;
  },

  search: async (caseNumber, referenceLetterNumber) => {
    const params = {};
    if (caseNumber) params.caseNumber = caseNumber;
    if (referenceLetterNumber) params.referenceLetterNumber = referenceLetterNumber;
    const response = await apiClient.get('/audit-logs/search', { params });
    return response.data.data || response.data;
  },

  logReportPrint: async (caseId, reportType) => {
    const response = await apiClient.post('/audit-logs/log-print', {
      caseId,
      reportType, // 'technical' or 'photo'
    });
    return response.data;
  },
};

// Statistics Service
export const statisticsService = {
  getStatistics: async (filters = {}) => {
    const params = {};
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (filters.locationId) params.locationId = filters.locationId;
    if (filters.region) params.region = filters.region;
    if (filters.userId) params.userId = filters.userId;
    if (filters.categoryId) params.categoryId = filters.categoryId;
    if (filters.typeId) params.typeId = filters.typeId;
    const response = await apiClient.get('/statistics', { params });
    return response.data.data || response.data;
  },
  getRegionsAndLocationsStats: async (filters = {}) => {
    const params = {};
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (filters.locationId) params.locationId = filters.locationId;
    if (filters.region) params.region = filters.region;
    if (filters.userId) params.userId = filters.userId;
    if (filters.categoryId) params.categoryId = filters.categoryId;
    if (filters.typeId) params.typeId = filters.typeId;
    const response = await apiClient.get('/statistics/regions-locations', { params });
    return response.data.data || response.data;
  },
  getCategoriesStats: async (filters = {}) => {
    const params = {};
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (filters.locationId) params.locationId = filters.locationId;
    if (filters.region) params.region = filters.region;
    if (filters.userId) params.userId = filters.userId;
    if (filters.categoryId) params.categoryId = filters.categoryId;
    if (filters.typeId) params.typeId = filters.typeId;
    const response = await apiClient.get('/statistics/categories', { params });
    return response.data.data || response.data;
  },
  getCategoriesComparison: async (filters = {}) => {
    const params = {};
    if (filters.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters.dateTo) params.dateTo = filters.dateTo;
    if (filters.locationId) params.locationId = filters.locationId;
    if (filters.region) params.region = filters.region;
    if (filters.userId) params.userId = filters.userId;
    if (filters.categoryId) params.categoryId = filters.categoryId;
    if (filters.typeId) params.typeId = filters.typeId;
    const response = await apiClient.get('/statistics/categories-comparison', { params });
    return response.data.data || response.data;
  },
};

export const caseCategoriesService = {
  getAll: async (search = '') => {
    const params = {};
    if (search) params.search = search;
    const response = await apiClient.get('/case-categories', { params });
    return response.data.data || response.data;
  },
  getById: async (id) => {
    const response = await apiClient.get(`/case-categories/${id}`);
    return response.data.data || response.data;
  },
  create: async (categoryData) => {
    const response = await apiClient.post('/case-categories', categoryData);
    return response.data.data || response.data;
  },
  update: async (id, categoryData) => {
    const response = await apiClient.put(`/case-categories/${id}`, categoryData);
    return response.data.data || response.data;
  },
  delete: async (id) => {
    const response = await apiClient.delete(`/case-categories/${id}`);
    return response.data;
  },
};

export const caseTypesService = {
  getAll: async (categoryId = null, search = '') => {
    const params = {};
    if (categoryId) params.categoryId = categoryId;
    if (search) params.search = search;
    const response = await apiClient.get('/case-types', { params });
    return response.data.data || response.data;
  },
  getById: async (id) => {
    const response = await apiClient.get(`/case-types/${id}`);
    return response.data.data || response.data;
  },
  create: async (typeData) => {
    const response = await apiClient.post('/case-types', typeData);
    return response.data.data || response.data;
  },
  update: async (id, typeData) => {
    const response = await apiClient.put(`/case-types/${id}`, typeData);
    return response.data.data || response.data;
  },
  delete: async (id) => {
    const response = await apiClient.delete(`/case-types/${id}`);
    return response.data;
  },
};

export default apiClient;

